(() => {
  /**
   * App Proxy endpoint – POST /apps/proxy-api/stock
   *
   * Request:  { products: [{ sku: "…", quantity: N }, …] }
   * Response: { success: true, availability: { sku: bool }, data: { sku: stockQty } }
   *
   * - success=true  => backend responded correctly
   * - success=false => error (frontend blocks checkout and shows error modal)
   * - availability[sku]=true  => SKU available for requested quantity
   * - availability[sku]=false => SKU NOT available (frontend blocks checkout and shows insufficient modal)
   * - data[sku] => actual stock quantity available
   */
  const APP_PROXY_STOCK_CHECK_URL = "/apps/proxy-api/stock";

  const STATE = { inFlight: false };

  const STR = getStrings();
  const MODAL = getThemeModalEls();

  function normalizeSku(sku) {
    return String(sku || "").trim();
  }

  function shopRoot() {
    if (window.Shopify?.routes?.root) return window.Shopify.routes.root;
    return "/";
  }

  function localizedPath(path) {
    const root = shopRoot();
    const cleanPath = String(path || "").replace(/^\/+/, "");
    return `${root}${cleanPath}`;
  }

  function escapeHtml(str) {
    const s = String(str ?? "");
    return s
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function getStrings() {
    const el = document.getElementById("ErpStockCheckStrings");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || "{}") || {};
    } catch {
      return {};
    }
  }

  function getThemeModalEls() {
    const modal = document.getElementById("ErpStockCheckModal");
    if (!modal) return null;
    return {
      modal,
      titleEl: modal.querySelector("[data-erp-stock-title]"),
      bodyEl: modal.querySelector("[data-erp-stock-body]"),
      primaryBtn: modal.querySelector("[data-erp-stock-primary]")
    };
  }

  function formatTemplate(str, params) {
    return String(str || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      params?.[key] ?? ""
    );
  }

  function openThemeModal({ opener, title, bodyHtml, primaryLabel = "OK", onPrimary }) {
    if (!MODAL) return () => {};

    const { modal, titleEl, bodyEl, primaryBtn } = MODAL;

    if (titleEl) titleEl.textContent = title || "";
    if (bodyEl) bodyEl.innerHTML = bodyHtml || "";
    if (primaryBtn) primaryBtn.textContent = primaryLabel;

    if (primaryBtn) {
      primaryBtn.addEventListener("click", () => {
        onPrimary?.();
        modal.hide();
      }, { once: true });
    }

    modal.show(opener || document.activeElement);
    return () => modal.hide();
  }

  function showBlockingLoading(opener) {
    return openThemeModal({
      opener,
      title: STR.titleStockCheck || "",
      bodyHtml: `<p><span class="erp-stock-check-modal__spinner"></span>${escapeHtml(STR.loadingMessage || "Verifica disponibilità in corso...")}</p>`
    });
  }

  function showErrorModal(opener) {
    openThemeModal({
      opener,
      title: STR.errorTitle || "",
      bodyHtml:
        `<p>${escapeHtml(STR.errorLine1 || "")}</p>` +
        `<p>${escapeHtml(STR.errorLine2 || "")}</p>`,
      primaryLabel: STR.errorCta || "",
      onPrimary: () => (window.location.href = localizedPath("cart"))
    });
  }

  function renderInsufficientRowHtml({ template, title, variantTitle, requestedQty, availableTotal }) {
    if (template) {
      const rendered = formatTemplate(template, {
        title,
        variantTitle,
        requestedQty,
        availableTotal
      });
      if (rendered && !rendered.includes("<strong></strong>")) return rendered;
    }
  
    return (
      `<strong>${escapeHtml(title)}</strong>` +
      (variantTitle ? ` <span>${escapeHtml(variantTitle)}</span>` : "") +
      ` × ${escapeHtml(requestedQty)}` +
      ` — disponibili: ${escapeHtml(availableTotal)}`
    );
  }

  function showInsufficientModal(result, opener) {
    const list = result.insufficient.map(i => {
      const sku = normalizeSku(i.sku);
      return `<li>${renderInsufficientRowHtml({
        template: STR.skuLine,
        title: result.titleBySku[sku] || i.title || "",
        variantTitle: result.variantTitleBySku[sku] || "",
        requestedQty: i.requestedQty,
        availableTotal: i.availableTotal
      })}</li>`;
    }).join("");

    openThemeModal({
      opener,
      title: STR.insufficientTitle || "",
      bodyHtml:
        `<p>${escapeHtml(STR.insufficientIntro || "")}</p>` +
        `<ul>${list}</ul>`,
      primaryLabel: STR.insufficientCta || "",
      onPrimary: () => (window.location.href = localizedPath("cart"))
    });
  }

  function isCheckoutSubmit(form, submitter) {
    if (!form || !submitter) return false;
    return submitter.getAttribute("name") === "checkout";
  }

  function isCheckoutLink(target) {
    return target instanceof Element &&
      target.closest('a[href$="/checkout"], a[href*="/checkout?"]');
  }

  // ---------------- VALIDAZIONE ----------------

  async function validateCartStock() {
    const res = await fetch(localizedPath("cart.js"));
    const cart = await res.json();

    const qtyBySku = new Map();
    const titleBySku = {};
    const variantTitleBySku = {};

    for (const item of cart.items || []) {
      const sku = normalizeSku(item.sku);
      if (!sku) continue;

      qtyBySku.set(sku, (qtyBySku.get(sku) || 0) + item.quantity);
      titleBySku[sku] ||= item.product_title;
      variantTitleBySku[sku] ||= item.variant_title;
    }

    const products = Array.from(qtyBySku.entries()).map(([sku, quantity]) => ({ sku, quantity }));

    const r = await fetch(APP_PROXY_STOCK_CHECK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ products })
    });
    const data = await r.json();

    if (!data.success) {
      throw new Error("Backend returned success=false");
    }

    const availability = data.availability || {};
    const stockData = data.data || {};
    const insufficient = [];

    for (const [sku, qty] of qtyBySku) {
      if (availability[sku] === false) {
        insufficient.push({
          sku,
          requestedQty: qty,
          availableTotal: stockData[sku] ?? 0
        });
      }
    }

    return {
      ok: insufficient.length === 0,
      insufficient,
      titleBySku,
      variantTitleBySku
    };
  }

  // ---------------- FLOW ----------------

  async function runStockGate({ opener, redirectOnOk = true }) {
    if (STATE.inFlight) return;
    STATE.inFlight = true;

    const close = redirectOnOk ? showBlockingLoading(opener) : null;

    try {
      const result = await validateCartStock();
      if (close) close();

      if (result.ok) {
        if (redirectOnOk) window.location.href = localizedPath("checkout");
        return;
      }

      showInsufficientModal(result, opener);
    } catch (e) {
      if (close) close();
      if (redirectOnOk) showErrorModal(opener);
    } finally {
      STATE.inFlight = false;
    }
  }

  function handleCheckoutSubmit(e) {
    const form = e.target;
    const submitter = e.submitter;

    if (!isCheckoutSubmit(form, submitter)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    runStockGate({ opener: submitter });
  }

  function handleCheckoutClick(e) {
    const link = isCheckoutLink(e.target);
    if (!link) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    runStockGate({ opener: link });
  }

  document.addEventListener("submit", handleCheckoutSubmit, true);
  document.addEventListener("click", handleCheckoutClick, true);

  if (window.location.pathname === localizedPath("cart").replace(/\/$/, "")) {
    runStockGate({ redirectOnOk: false });
  }
})();